import React, { useContext, useEffect, useState } from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { selectDetail } from 'store/Contextualization/AssetDetail/selectors';
import { FormattedMessage } from 'react-intl';
import { getHosts, getServices } from 'Screens/Contextualization/ManageEditCreate/actions/Actions';
import { unSelectAll, hideManageDetail, getVulnsByAssetId } from 'store/Contextualization/AssetDetail/actions';
import { selectCurrentHost } from 'store/Host/selectors';
import { selectServicesAndHostsContextMove } from 'store/ManageEditCreate/selectors';
import { updateHostParent } from 'store/Manage/actions';
import {
  Wrapper, FieldContainer, FieldName, FieldValue
} from 'Screens/Contextualization/Manage/components/VulnerabilityDetail/components/Tabs/components/GeneralTab/components/HostData/styled';
import { FEATURE_VULNS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { MODAL_MOVE_VULN_HOST } from 'store/modals/modals';
import { MoveIcon, Flex, Footer, Title, Subtitle, ModalContent, AssetIcon } from './styled';
import { closeModal, openModal } from 'store/modals/actions';
import ModalWrapper from 'Common/Components/ModalWrapper';
import { Dropdown } from 'Common/Components/Inputs';
import { dropdownBlueStyle } from 'Common/Components/Inputs/styled';
import Button from 'shared/ui/Button';
import CancelButton from 'shared/ui/CancelButton';
import useKeyPress from 'Hooks/useKeyPress';
import DateFormatter from 'Screens/Contextualization/Manage/components/VulnerabilityDetail/components/Tabs/components/GeneralTab/components/Date';
import { ThemeContext } from 'Context';
import moment from 'moment';

// eslint-disable-next-line import/prefer-default-export

export const HostData = () => {
  const dispatch = useDispatch();
  const isEnabledMove = useSelector((state) => selectAllowedByRole(state, FEATURE_VULNS, PERMISSION_TYPES.UPDATE));
  const handleMoveIconClick = () => {
    dispatch(openModal(MODAL_MOVE_VULN_HOST));
  };
  return (
    <Wrapper>
      <Target />
      <Service />
      <Date />
      <LastDetected />
      {isEnabledMove && <MoveIcon title="Move to a different Asset" onClick={ handleMoveIconClick } />}
    </Wrapper>
  );
};

export const MoveVulnModal = () => {
  const dispatch = useDispatch();
  const vulnDetail = useSelector((state) => selectDetail('vulns', state));
  const [assets, setAssets] = useState([]);
  const servicesAndHost = useSelector(selectServicesAndHostsContextMove);
  const isWeb = get(vulnDetail, 'type', '') === 'VulnerabilityWeb';
  const currentHost = useSelector(selectCurrentHost);
  const assetId = get(currentHost, 'id', 0);
  const { themeStyles } = useContext(ThemeContext);
  const currentVuln = {
    value: {
      hostId: assetId,
      name: vulnDetail.service?.name || vulnDetail.target,
      type: vulnDetail.type,
      parentId: vulnDetail.parent
    },
    label: vulnDetail.service ? `(${vulnDetail.service.name}) - ${vulnDetail.target}` : vulnDetail.target
  };
  const [selectedAsset, setSelectedAsset] = useState(currentVuln);

  const parseLabel = (item) => {
    if (item.type && item.type === 'Host') {
      return item.name;
    }
    if (item.type && item.type === 'Service') {
      return `(${item.name}) - ${item.hostname}`;
    }

    return item.name;
  };

  const parseHostAndServices = (data) => {
    const filteredData = data.filter(item => {
      if (isWeb) {
        return item.type === 'Service';
      } else if (!isWeb && (vulnDetail?.parent_type === 'Service')) {
        return item;
      } else {
        return item.type === 'Host';
      }
    });
    const result = filteredData.map(item => {
      const parentId = item.id;
      const hostId = isWeb ? item.host_id : item.id;
      return { value: { parentId, type: item.type, name: item.name, hostId }, label: parseLabel(item) };
    });
    return result;
  };

  useEffect(() => {
    setAssets(parseHostAndServices(servicesAndHost));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesAndHost]);

  useEffect(() => {
    dispatch(getServices());
    dispatch(getHosts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => dispatch(getVulnsByAssetId(currentVuln.value.hostId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAssetSelect = (asset) => {
    setSelectedAsset(asset);
  };

  const handleAccept = () => {
    const newParent = selectedAsset.value.parentId;
    const newParentType = selectedAsset.value.type;
    dispatch(updateHostParent(vulnDetail._id, newParent, newParentType, vulnDetail.workspace_name));
    if (selectedAsset.value.hostId !== assetId) {
      dispatch(unSelectAll('vulns'));
      dispatch(hideManageDetail());
    }
    dispatch(closeModal(MODAL_MOVE_VULN_HOST));
  };

  const handleClose = () => {
    dispatch(closeModal(MODAL_MOVE_VULN_HOST));
  };

  useKeyPress(() => {
    handleClose();
  }, ['Escape']);

  useKeyPress(() => {
    handleAccept();
  }, ['Enter']);

  return (
    <ModalWrapper>
      <ModalContent>
        <Flex>
          <AssetIcon />
          <Title>Move Vulnerability</Title>
        </Flex>
        <Subtitle>Select the Asset where you want to move this vulnerability to</Subtitle>
        <Dropdown
          style={ dropdownBlueStyle(themeStyles) }
          options={ assets }
          defaultValue={ currentVuln }
          onChange={ handleAssetSelect }
        />
        <Footer>
            <Button
              onClick={ handleAccept }
              disabled={ selectedAsset.value.parentId === currentVuln.value.parentId }
              children="Move"
            />
            <CancelButton onClick={ handleClose } children="Close" />
        </Footer>
      </ModalContent>
    </ModalWrapper>
  );
};

const Target = () => {
  const vuln = useSelector((state) => selectDetail('vulns', state));
  const target = get(vuln, 'target', '');

  return (
    <FieldContainer>
      <FieldName>
        <FormattedMessage id="manage.detail.tab.general.hostData.host" />
      </FieldName>
      <FieldValue title={ target }>{target}</FieldValue>
    </FieldContainer>
  );
};

const Service = () => {
  const vuln = useSelector((state) => selectDetail('vulns', state));
  const service = get(vuln, 'service', null);

  if (!service) return null;
  return (
    <FieldContainer>
      <FieldName>
        <FormattedMessage id="manage.detail.tab.general.service" />
      </FieldName>
      <FieldValue>{service.summary}</FieldValue>
    </FieldContainer>
  );
};

const Date = () => {
  const vuln = useSelector((state) => selectDetail('vulns', state));
  const createTime = get(vuln, 'metadata.create_time', '');

  return (
    <FieldContainer>
      <FieldName>
        <FormattedMessage id="manage.detail.tab.general.hostData.date" />
      </FieldName>
      <FieldValue><DateFormatter date={ createTime } /></FieldValue>
    </FieldContainer>
  );
};

const LastDetected = () => {
  const vuln = useSelector((state) => selectDetail('vulns', state));
  const createTime = get(vuln, 'metadata.create_time', '');
  const lastDetected = get(vuln, 'last_detected', '');
  const shouldShowLastDetected = Boolean(createTime && lastDetected && !moment(lastDetected).isSame(createTime, 'day'));

  if (!shouldShowLastDetected) return null;

  return (
    <FieldContainer>
      <FieldName>
        <FormattedMessage id="manage.detail.tab.general.hostData.lastDetected" />
      </FieldName>
      <FieldValue>
        <DateFormatter date={ lastDetected } />
      </FieldValue>
    </FieldContainer>
  );
};
